import type {ReactElement} from 'react';
import React from 'react';
import type {DropdownItemStorage} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {ContextualMenuDropdownOption} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {LayoutBackgroundColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-color-option';
import {LayoutBackgroundColorOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-color-opacity-option';
import {LayoutBackgroundTypes} from '@PosterWhiteboard/items/layouts/layout.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';
import type {RGBA} from '@Utils/color.util';

interface LayoutBackgroundOptionProps {
  onClickColorFill(): void;
}

export function LayoutBackgroundOption({...props}: LayoutBackgroundOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemBackgroundType = useGetKeyFromTableItem('backgroundType');
  const activeItemBackgroundColor = useGetKeyFromTableItem('itemBackgroundColor');
  if (!activeItem || (activeItem && !('backgroundType' in activeItem)) || activeItemBackgroundType === undefined || !activeItemBackgroundColor) {
    throw new Error('Item doesnt have backgroundType property');
  }

  const getDropdownItems = (): DropdownItemStorage[] => {
    return [
      {
        id: LayoutBackgroundTypes.NONE,
        text: window.i18next.t('pmwjs_transparent'),
      },
      {
        id: LayoutBackgroundTypes.COLOR,
        text: window.i18next.t('pmwjs_color'),
      },
    ];
  };

  const getSelectedItem = (): LayoutBackgroundTypes => {
    return activeItemBackgroundType;
  };

  const onClickHandler = (id: LayoutBackgroundTypes): void => {
    if (getSelectedItem() === id) {
      return;
    }

    void activeItem.updateFromObject({
      backgroundType: Number(id),
    });
  };

  const getColorOption = (): ReactElement<any> => {
    return (
      <LayoutBackgroundColorOption
        backgroundColor={activeItemBackgroundColor}
        onClickColorFill={(): void => {
          props.onClickColorFill();
        }}
      />
    );
  };

  const updateOpacity = (backgroundColorAlpha: number, undoable = true): void => {
    const backgroundColor = activeItemBackgroundColor.slice() as RGBA;
    backgroundColor[3] = backgroundColorAlpha;
    void activeItem.updateFromObject(
      {
        itemBackgroundColor: backgroundColor,
      },
      {
        undoable,
      }
    );
  };

  const getOpacityOption = (): ReactElement<any> => {
    return <LayoutBackgroundColorOpacityOption backgroundColorAlpha={activeItemBackgroundColor[3]} updateOpacity={updateOpacity} />;
  };

  const getSubOptionsForColoredBackground = (): ReactElement<any> => {
    return (
      <>
        {getColorOption()}
        {getOpacityOption()}
      </>
    );
  };

  const getSubOptions = (): ReactElement<any> | null => {
    if (getSelectedItem() === LayoutBackgroundTypes.NONE) {
      return null;
    }

    return getSubOptionsForColoredBackground();
  };

  return (
    <ContextualMenuDropdownOption
      id="menu-background-option"
      label={window.i18next.t('pmwjs_background')}
      dropdownItems={getDropdownItems()}
      onClick={onClickHandler}
      selectedItemId={getSelectedItem()}
    >
      {getSubOptions()}
    </ContextualMenuDropdownOption>
  );
}
